<template>
  <div class="container-fluid py-1">
    <div class="row">
      <div class="col-12">
        <div class="card my-4" style="min-height: 500px; display: flex; flex-direction: column">
          <!-- Card Header -->
          <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
            <div
              class="bg-gradient-danger shadow-danger border-radius-lg pt-2 pb-3 d-flex justify-content-between align-items-center px-3">
              <div>
                <h6 class="text-white text-capitalize mb-0">Report Errors</h6>
                <p class="text-white text-sm mb-0 opacity-8">
                  Track issues detected during report processing
                </p>
              </div>
              <div class="d-flex align-items-center gap-2">
                <span v-if="unreadCount > 0" class="unread-badge">
                  {{ unreadCount }} unread
                </span>
                <button v-if="unreadCount > 0" class="btn btn-sm btn-outline-white mb-0" @click="markAllRead"
                  :disabled="markingAllRead">
                  <i class="material-icons text-sm me-1">done_all</i>
                  Mark all read
                </button>
              </div>
            </div>
          </div>

          <!-- Filter Bar -->
          <div class="px-4 pt-3 pb-0">
            <div class="row align-items-center">
              <div class="col-md-4 col-sm-12 mb-2">
                <div class="filter-group">
                  <label class="filter-label">Severity</label>
                  <div class="btn-group btn-group-sm w-100" role="group">
                    <button type="button" class="btn filter-btn" :class="{ active: filters.severity === '' }"
                      @click="setSeverityFilter('')">
                      All
                    </button>
                    <button type="button" class="btn filter-btn" :class="{ active: filters.severity === 'CRITICAL' }"
                      @click="setSeverityFilter('CRITICAL')">
                      <span class="severity-dot critical"></span> Critical
                    </button>
                    <button type="button" class="btn filter-btn" :class="{ active: filters.severity === 'WARNING' }"
                      @click="setSeverityFilter('WARNING')">
                      <span class="severity-dot warning"></span> Warning
                    </button>
                    <button type="button" class="btn filter-btn" :class="{ active: filters.severity === 'INFO' }"
                      @click="setSeverityFilter('INFO')">
                      <span class="severity-dot info"></span> Info
                    </button>
                  </div>
                </div>
              </div>
              <div class="col-md-3 col-sm-6 mb-2">
                <div class="filter-group">
                  <label class="filter-label">Status</label>
                  <div class="btn-group btn-group-sm w-100" role="group">
                    <button type="button" class="btn filter-btn" :class="{ active: filters.is_read === '' }"
                      @click="setReadFilter('')">
                      All
                    </button>
                    <button type="button" class="btn filter-btn" :class="{ active: filters.is_read === 'false' }"
                      @click="setReadFilter('false')">
                      Unread
                    </button>
                    <button type="button" class="btn filter-btn" :class="{ active: filters.is_read === 'true' }"
                      @click="setReadFilter('true')">
                      Read
                    </button>
                  </div>
                </div>
              </div>
              <div class="col-md-5 col-sm-6 mb-2 text-end">
                <div class="stats-summary d-flex gap-3 justify-content-end">
                  <div class="stat-chip critical-chip">
                    <i class="material-icons">error</i>
                    <span>{{ totalCount }} total</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Card Body -->
          <div class="card-body px-0 pb-2" style="flex: 1">
            <!-- Loading State -->
            <div v-if="isLoading" class="text-center py-5 state-container">
              <div class="spinner-border text-danger" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="mt-3 text-muted">Loading errors...</p>
            </div>

            <!-- Error State -->
            <div v-else-if="hasError" class="text-center py-5 state-container">
              <i class="material-icons error-icon text-warning">cloud_off</i>
              <h5 class="text-warning mt-3">Failed to load errors</h5>
              <p class="text-muted">Please try again later.</p>
              <button class="btn btn-sm btn-danger" @click="loadErrors">
                <i class="material-icons text-sm me-1">refresh</i>
                Retry
              </button>
            </div>

            <!-- Empty State -->
            <div v-else-if="errors.length === 0" class="text-center py-5 state-container">
              <i class="material-icons empty-icon text-success">check_circle</i>
              <h5 class="text-muted mt-3">No errors found</h5>
              <p class="text-muted">
                All report submissions are running smoothly!
              </p>
            </div>

            <!-- Error List -->
            <div v-else class="error-list px-3">
              <div v-for="error in errors" :key="error.id" class="error-card" :class="{
                'unread': !error.is_read,
                'read': error.is_read,
              }" @click="openErrorDetail(error)">
                <!-- Unread indicator -->
                <div class="unread-indicator" v-if="!error.is_read"></div>

                <div class="error-card-content">
                  <!-- Left: Icon + Info -->
                  <div class="error-main">
                    <div class="error-icon-wrapper" :class="getSeverityClass(error.severity)">
                      <i class="material-icons">{{ getSeverityIcon(error.severity) }}</i>
                    </div>
                    <div class="error-info">
                      <div class="error-title-row">
                        <h6 class="error-title mb-0">{{ formatErrorType(error.error_type) }}</h6>
                        <span class="severity-badge" :class="getSeverityBadgeClass(error.severity)">
                          {{ error.severity }}
                        </span>
                      </div>
                      <p class="error-message text-sm text-secondary mb-0">
                        {{ getErrorSummary(error) }}
                      </p>
                      <div class="error-meta mt-1">
                        <span v-if="error.report_id" class="meta-tag">
                          <i class="material-icons">description</i>
                          {{ error.report_id }}
                        </span>
                        <span class="meta-tag">
                          <i class="material-icons">schedule</i>
                          {{ formatTimeAgo(error.created_at) }}
                        </span>
                        <span v-if="error.email_sent" class="meta-tag email-sent">
                          <i class="material-icons">email</i>
                          Email sent
                        </span>
                      </div>
                    </div>
                  </div>

                  <!-- Right: Actions -->
                  <div class="error-actions">
                    <button v-if="!error.is_read" class="btn-mark-read" @click.stop="markAsRead(error)"
                      title="Mark as read">
                      <i class="material-icons">visibility</i>
                    </button>
                    <button class="btn-view-detail" @click.stop="openErrorDetail(error)" title="View details">
                      <i class="material-icons">open_in_new</i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Card Footer - Pagination -->
          <div class="card-footer px-3" style="margin-top: auto">
            <div v-if="errors.length > 0">
              <div class="d-flex justify-content-between align-items-center">
                <p class="mb-0 text-sm text-secondary">
                  Showing
                  {{ (pagination.page - 1) * pagination.page_size + 1 }}
                  to
                  {{ Math.min(pagination.page * pagination.page_size, pagination.total_count) }}
                  of {{ pagination.total_count }} errors
                </p>
                <nav>
                  <ul class="pagination pagination-sm mb-0">
                    <li class="page-item" :class="{ disabled: !pagination.has_previous }">
                      <a class="page-link" href="#" @click.prevent="goToPage(pagination.page - 1)">
                        <i class="material-icons text-sm">chevron_left</i>
                      </a>
                    </li>
                    <li v-for="page in displayedPages" :key="page" class="page-item"
                      :class="{ active: page === pagination.page }">
                      <a class="page-link" href="#" @click.prevent="goToPage(page)">
                        {{ page }}
                      </a>
                    </li>
                    <li class="page-item" :class="{ disabled: !pagination.has_next }">
                      <a class="page-link" href="#" @click.prevent="goToPage(pagination.page + 1)">
                        <i class="material-icons text-sm">chevron_right</i>
                      </a>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
            <div v-else class="text-center text-muted">
              <small>No records to display</small>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Error Detail Modal -->
    <teleport to="body">
      <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
        <div class="modal-container" @click.stop>
          <!-- Modal Header -->
          <div class="modal-header-custom" :class="getSeverityClass(selectedError?.severity)">
            <div class="modal-header-content">
              <div class="modal-icon-wrapper">
                <i class="material-icons">{{ getSeverityIcon(selectedError?.severity) }}</i>
              </div>
              <div>
                <h5 class="modal-title mb-0">{{ formatErrorType(selectedError?.error_type) }}</h5>
                <span class="severity-badge mt-1" :class="getSeverityBadgeClass(selectedError?.severity)"
                  style="color: #fff; background: rgba(255,255,255,0.2);">
                  {{ selectedError?.severity }}
                </span>
              </div>
            </div>
            <button class="modal-close-btn" @click="closeModal">
              <i class="material-icons">close</i>
            </button>
          </div>

          <!-- Modal Body -->
          <div class="modal-body-custom">
            <!-- Summary Section -->
            <div class="detail-section">
              <h6 class="section-title">
                <i class="material-icons">info</i> Summary
              </h6>
              <div class="detail-grid">
                <div class="detail-item">
                  <span class="detail-label">Error Type</span>
                  <span class="detail-value">{{ selectedError?.error_type }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Severity</span>
                  <span class="severity-badge" :class="getSeverityBadgeClass(selectedError?.severity)">
                    {{ selectedError?.severity }}
                  </span>
                </div>
                <div class="detail-item" v-if="selectedError?.report_id">
                  <span class="detail-label">Report</span>
                  <span class="detail-value">{{ selectedError?.report_id }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Occurred At</span>
                  <span class="detail-value">{{ formatDateTime(selectedError?.created_at) }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Email Notification</span>
                  <span class="detail-value">
                    <span v-if="selectedError?.email_sent" class="text-success">
                      <i class="material-icons text-sm">check_circle</i> Sent
                    </span>
                    <span v-else class="text-secondary">
                      <i class="material-icons text-sm">remove_circle_outline</i> Not sent
                    </span>
                  </span>
                </div>
                <div class="detail-item" v-if="selectedError?.ai_used">
                  <span class="detail-label">AI Assistance</span>
                  <span class="detail-value">{{ selectedError?.ai_purpose || 'Used' }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Status</span>
                  <span class="detail-value">
                    <span v-if="selectedError?.is_read" class="text-muted">
                      <i class="material-icons text-sm">visibility</i> Read
                      <span v-if="selectedError?.read_at" class="text-xs ms-1">
                        ({{ formatDateTime(selectedError.read_at) }})
                      </span>
                    </span>
                    <span v-else class="text-danger fw-bold">
                      <i class="material-icons text-sm">fiber_new</i> Unread
                    </span>
                  </span>
                </div>
              </div>
            </div>

            <!-- Context Details Section -->
            <div class="detail-section" v-if="selectedError?.context">
              <h6 class="section-title">
                <i class="material-icons">data_object</i> Error Details
              </h6>
              <div class="context-details">
                <div v-for="(value, key) in filteredContext" :key="key" class="context-item">
                  <span class="context-key">{{ formatContextKey(key) }}</span>
                  <span class="context-value" :class="{ 'context-long': isLongValue(value) }">
                    {{ formatContextValue(value) }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Modal Footer -->
          <div class="modal-footer-custom">
            <button v-if="selectedError && !selectedError.is_read" class="btn btn-sm btn-danger me-2"
              @click="markAsRead(selectedError); closeModal()">
              <i class="material-icons text-sm me-1">visibility</i>
              Mark as Read
            </button>
            <button class="btn btn-sm btn-outline-secondary" @click="closeModal">
              Close
            </button>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "ErrorsView",
  data() {
    return {
      isLoading: false,
      hasError: false,
      errors: [],
      unreadCount: 0,
      totalCount: 0,
      markingAllRead: false,
      pagination: {
        page: 1,
        page_size: 50,
        total_count: 0,
        total_pages: 1,
        has_next: false,
        has_previous: false,
      },
      filters: {
        severity: "",
        is_read: "",
      },
      showModal: false,
      selectedError: null,
    };
  },
  computed: {
    displayedPages() {
      const pages = [];
      const maxVisible = 5;
      const total = this.pagination.total_pages;
      if (total <= 0) return pages;

      let start = Math.max(1, this.pagination.page - Math.floor(maxVisible / 2));
      let end = Math.min(total, start + maxVisible - 1);
      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    },
    filteredContext() {
      if (!this.selectedError?.context) return {};
      const exclude = ["processing_status"];
      const ctx = {};
      for (const [key, value] of Object.entries(this.selectedError.context)) {
        if (!exclude.includes(key) && value !== null && value !== undefined && value !== "") {
          ctx[key] = value;
        }
      }
      return ctx;
    },
  },
  created() {
    this.loadErrors();
  },
  methods: {
    async loadErrors() {
      this.isLoading = true;
      this.hasError = false;

      try {
        const params = {
          page: this.pagination.page,
          page_size: this.pagination.page_size,
        };
        if (this.filters.severity) params.severity = this.filters.severity;
        if (this.filters.is_read) params.is_read = this.filters.is_read;

        const response = await axios.get("/errors/", { params });

        if (response.data) {
          this.errors = response.data.results || [];
          this.pagination = response.data.pagination || this.pagination;
          this.unreadCount = response.data.unread_count || 0;
          this.totalCount = this.pagination.total_count || 0;
        }
      } catch (error) {
        console.error("Error loading errors:", error);
        this.hasError = true;
        this.showNotification("Failed to load errors", "error");
      } finally {
        this.isLoading = false;
      }
    },

    async markAsRead(error) {
      if (error.is_read) return;

      try {
        await axios.patch(`/errors/${error.id}/read/`);
        error.is_read = true;
        error.read_at = new Date().toISOString();
        this.unreadCount = Math.max(0, this.unreadCount - 1);
        this.showNotification("Error marked as read", "success");
      } catch (err) {
        console.error("Error marking as read:", err);
        this.showNotification("Failed to mark as read", "error");
      }
    },

    async markAllRead() {
      this.markingAllRead = true;
      try {
        const response = await axios.patch("/errors/mark-all-read/");
        this.errors.forEach((e) => {
          e.is_read = true;
          e.read_at = new Date().toISOString();
        });
        this.unreadCount = 0;
        const count = response.data?.updated_count || 0;
        this.showNotification(`${count} errors marked as read`, "success");
      } catch (err) {
        console.error("Error marking all as read:", err);
        this.showNotification("Failed to mark all as read", "error");
      } finally {
        this.markingAllRead = false;
      }
    },

    goToPage(page) {
      if (page < 1 || page > this.pagination.total_pages) return;
      this.pagination.page = page;
      this.loadErrors();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },

    setSeverityFilter(severity) {
      this.filters.severity = severity;
      this.pagination.page = 1;
      this.loadErrors();
    },

    setReadFilter(readStatus) {
      this.filters.is_read = readStatus;
      this.pagination.page = 1;
      this.loadErrors();
    },

    openErrorDetail(error) {
      this.selectedError = error;
      this.showModal = true;
      document.body.style.overflow = "hidden";

      // Auto-mark as read when opened
      if (!error.is_read) {
        this.markAsRead(error);
      }
    },

    closeModal() {
      this.showModal = false;
      this.selectedError = null;
      document.body.style.overflow = "";
    },

    // Formatting helpers
    formatErrorType(type) {
      if (!type) return "Unknown Error";
      const typeMap = {
        DATA_MISMATCH: "Data Structure Mismatch",
        DUPLICATE_DATA: "Duplicate Data Detected",
        MISSING_DATA: "Missing Data",
        NCEMS_NOT_REACHABLE: "NCEMS System Unreachable",
        NCEMS_AUTHENTICATION_FAILED: "NCEMS Authentication Failed",
        SUBMISSION_REMINDER: "Submission Reminder",
        PACKAGE_ALREADY_EXISTS: "Package Already Exists",
        NCEMS_UNKNOWN_ERROR: "NCEMS Unknown Error",
        NCEMS_HTTP_ERROR: "NCEMS HTTP Error",
        INVALID_PACKAGE_DATE_ORDER: "Invalid Package Date Order",
        CONSECUTIVE_UPLOAD_FAILURES: "Consecutive Upload Failures",
      };
      return typeMap[type] || type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    },

    getErrorSummary(error) {
      const ctx = error.context || {};
      if (ctx.error_message) return ctx.error_message;
      if (ctx.action_taken) return ctx.action_taken;
      if (ctx.action_required) return ctx.action_required;
      if (ctx.impact) return ctx.impact;
      return `${this.formatErrorType(error.error_type)} detected`;
    },

    getSeverityIcon(severity) {
      const icons = {
        CRITICAL: "error",
        WARNING: "warning",
        INFO: "info",
      };
      return icons[severity] || "help_outline";
    },

    getSeverityClass(severity) {
      const classes = {
        CRITICAL: "severity-critical",
        WARNING: "severity-warning",
        INFO: "severity-info",
      };
      return classes[severity] || "";
    },

    getSeverityBadgeClass(severity) {
      const classes = {
        CRITICAL: "badge-critical",
        WARNING: "badge-warning",
        INFO: "badge-info",
      };
      return classes[severity] || "";
    },

    formatTimeAgo(dateStr) {
      if (!dateStr) return "N/A";
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: diffDays > 365 ? "numeric" : undefined,
      });
    },

    formatDateTime(dateStr) {
      if (!dateStr) return "N/A";
      const date = new Date(dateStr);
      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },

    formatContextKey(key) {
      return key
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
    },

    formatContextValue(value) {
      if (value === null || value === undefined) return "—";
      if (typeof value === "object") return JSON.stringify(value, null, 2);
      return String(value);
    },

    isLongValue(value) {
      if (typeof value === "object") return true;
      return String(value).length > 80;
    },

    showNotification(message, type = "info") {
      const alertClass =
        {
          success: "alert-success",
          error: "alert-danger",
          warning: "alert-warning",
          info: "alert-info",
        }[type] || "alert-info";

      const icon = {
        success: "check_circle",
        error: "error",
        warning: "warning",
        info: "info",
      }[type] || "info";

      const notification = document.createElement("div");
      notification.className = `alert ${alertClass} alert-dismissible fade show position-fixed toast-notification`;
      notification.innerHTML = `
        <i class="material-icons text-sm me-2 align-middle">${icon}</i>
        <span class="align-middle">${message}</span>
        <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
      `;
      document.body.appendChild(notification);
      setTimeout(() => {
        notification.classList.remove("show");
        setTimeout(() => notification.remove(), 300);
      }, 3000);
    },
  },
};
</script>

<style scoped>
/* ==============================
   Layout & Card
   ============================== */
.container-fluid {
  min-height: calc(100vh - 60px);
}

.card {
  min-height: 500px;
  display: flex;
  flex-direction: column;
  border: none;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
}

.card-body {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.card-footer {
  margin-top: auto;
  background-color: transparent;
  border-top: 1px solid #e9ecef;
}

/* ==============================
   Header
   ============================== */
.bg-gradient-danger {
  background: linear-gradient(310deg, #d32f2f 0%, #f44336 100%);
}

.shadow-danger {
  box-shadow:
    0 4px 20px 0 rgba(244, 67, 53, 0.14),
    0 7px 10px -5px rgba(239, 83, 80, 0.4);
}

.btn-outline-white {
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.6);
  background: rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  transition: all 0.2s;
}

.btn-outline-white:hover {
  background: rgba(255, 255, 255, 0.25);
  color: #fff;
  border-color: #fff;
}

.unread-badge {
  background: #fff;
  color: #d32f2f;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 20px;
  letter-spacing: 0.02em;
}

/* ==============================
   Filter Bar
   ============================== */
.filter-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.filter-label {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #9e9e9e;
  margin-bottom: 0;
}

.filter-btn {
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  color: #616161;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 4px 12px;
  transition: all 0.2s;
}

.filter-btn:hover {
  background: #eeeeee;
  color: #424242;
}

.filter-btn.active {
  background: #d32f2f;
  border-color: #d32f2f;
  color: #fff;
  box-shadow: 0 2px 8px rgba(211, 47, 47, 0.3);
}

.severity-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 2px;
}

.severity-dot.critical {
  background-color: #f44336;
}

.severity-dot.warning {
  background-color: #ff9800;
}

.severity-dot.info {
  background-color: #2196f3;
}

.stat-chip {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #616161;
  background: #f5f5f5;
  padding: 4px 12px;
  border-radius: 20px;
}

.stat-chip .material-icons {
  font-size: 16px;
}

.critical-chip {
  color: #d32f2f;
  background: #ffebee;
}

/* ==============================
   Error List
   ============================== */
.error-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.error-card {
  position: relative;
  border: 1px solid #e8e8e8;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.25s ease;
  background: #fff;
  overflow: hidden;
}

.error-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  border-color: #d0d0d0;
}

/* Unread styling */
.error-card.unread {
  background: linear-gradient(135deg, #fff8f8 0%, #ffffff 40%);
  border-left: 3px solid #f44336;
  box-shadow: 0 2px 8px rgba(244, 67, 53, 0.06);
}

.error-card.unread:hover {
  box-shadow: 0 4px 16px rgba(244, 67, 53, 0.12);
}

/* Read styling */
.error-card.read {
  opacity: 0.75;
}

.error-card.read:hover {
  opacity: 1;
}

.unread-indicator {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 10px;
  height: 10px;
  background: #f44336;
  border-radius: 50%;
  animation: pulse-dot 2s infinite;
  z-index: 2;
}

@keyframes pulse-dot {

  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(244, 67, 53, 0.5);
  }

  50% {
    box-shadow: 0 0 0 6px rgba(244, 67, 53, 0);
  }
}

.error-card-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
}

.error-main {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  flex: 1;
  min-width: 0;
}

.error-icon-wrapper {
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.error-icon-wrapper .material-icons {
  font-size: 22px;
  color: #fff;
}

.error-icon-wrapper.severity-critical {
  background: linear-gradient(135deg, #e53935, #f44336);
  box-shadow: 0 2px 8px rgba(244, 67, 53, 0.3);
}

.error-icon-wrapper.severity-warning {
  background: linear-gradient(135deg, #f57c00, #ff9800);
  box-shadow: 0 2px 8px rgba(255, 152, 0, 0.3);
}

.error-icon-wrapper.severity-info {
  background: linear-gradient(135deg, #1976d2, #2196f3);
  box-shadow: 0 2px 8px rgba(33, 150, 243, 0.3);
}

.error-info {
  flex: 1;
  min-width: 0;
}

.error-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.error-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #212121;
}

.error-message {
  font-size: 0.8rem;
  color: #757575;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 500px;
}

.error-meta {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.meta-tag {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 0.7rem;
  color: #9e9e9e;
  font-weight: 500;
}

.meta-tag .material-icons {
  font-size: 13px;
}

.meta-tag.email-sent {
  color: #4caf50;
}

/* Severity Badges */
.severity-badge {
  display: inline-block;
  font-size: 0.6rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.badge-critical {
  background: #ffebee;
  color: #c62828;
}

.badge-warning {
  background: #fff3e0;
  color: #e65100;
}

.badge-info {
  background: #e3f2fd;
  color: #1565c0;
}

/* Action Buttons */
.error-actions {
  display: flex;
  gap: 6px;
  align-items: center;
  margin-left: 12px;
}

.btn-mark-read,
.btn-view-detail {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-mark-read {
  background: #e8f5e9;
  color: #388e3c;
}

.btn-mark-read:hover {
  background: #c8e6c9;
  transform: scale(1.1);
}

.btn-view-detail {
  background: #e3f2fd;
  color: #1976d2;
}

.btn-view-detail:hover {
  background: #bbdefb;
  transform: scale(1.1);
}

/* ==============================
   States
   ============================== */
.state-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.error-icon {
  font-size: 64px;
}

.empty-icon {
  font-size: 64px;
}

/* ==============================
   Pagination
   ============================== */
.pagination .page-item .page-link {
  border: none;
  color: #616161;
  font-size: 0.8rem;
  font-weight: 500;
  padding: 6px 12px;
  border-radius: 6px;
  margin: 0 2px;
  transition: all 0.2s;
}

.pagination .page-item .page-link .material-icons {
  font-size: 18px;
  line-height: 1;
}

.pagination .page-item.active .page-link {
  background: #d32f2f;
  color: #fff;
  box-shadow: 0 2px 8px rgba(211, 47, 47, 0.3);
}

.pagination .page-item:not(.active):not(.disabled) .page-link:hover {
  background: #ffebee;
  color: #d32f2f;
}

.pagination .page-item.disabled .page-link {
  color: #ccc;
}

/* ==============================
   Modal
   ============================== */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

.modal-container {
  background: #fff;
  border-radius: 16px;
  width: 90%;
  max-width: 680px;
  max-height: 85vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.2);
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }

  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-header-custom {
  padding: 20px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #fff;
}

.modal-header-custom.severity-critical {
  background: linear-gradient(135deg, #c62828, #f44336);
}

.modal-header-custom.severity-warning {
  background: linear-gradient(135deg, #e65100, #ff9800);
}

.modal-header-custom.severity-info {
  background: linear-gradient(135deg, #1565c0, #2196f3);
}

.modal-header-content {
  display: flex;
  align-items: center;
  gap: 14px;
}

.modal-icon-wrapper {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-icon-wrapper .material-icons {
  font-size: 26px;
}

.modal-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #fff;
}

.modal-close-btn {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;
}

.modal-close-btn:hover {
  background: rgba(255, 255, 255, 0.35);
}

.modal-body-custom {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.detail-section {
  margin-bottom: 24px;
}

.detail-section:last-child {
  margin-bottom: 0;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  font-weight: 700;
  color: #424242;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 14px;
  padding-bottom: 8px;
  border-bottom: 2px solid #f5f5f5;
}

.section-title .material-icons {
  font-size: 18px;
  color: #9e9e9e;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.detail-label {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #9e9e9e;
}

.detail-value {
  font-size: 0.85rem;
  font-weight: 500;
  color: #333;
}

.detail-value .material-icons {
  vertical-align: middle;
}

/* Context Details */
.context-details {
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: #fafafa;
  border-radius: 10px;
  padding: 16px;
  border: 1px solid #f0f0f0;
}

.context-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.context-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.context-key {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #9e9e9e;
}

.context-value {
  font-size: 0.85rem;
  color: #333;
  word-break: break-word;
}

.context-value.context-long {
  font-family: "SFMono-Regular", "Consolas", "Liberation Mono", "Menlo", monospace;
  font-size: 0.75rem;
  white-space: pre-wrap;
  background: #fff;
  padding: 8px 10px;
  border-radius: 6px;
  border: 1px solid #eee;
  max-height: 200px;
  overflow-y: auto;
}

/* Modal Footer */
.modal-footer-custom {
  padding: 14px 24px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  background: #fafafa;
}

/* ==============================
   Toast
   ============================== */
.toast-notification {
  top: 20px;
  right: 20px;
  z-index: 99999;
  min-width: 280px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
}

/* ==============================
   Responsive
   ============================== */
@media (max-width: 768px) {
  .error-card-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .error-actions {
    margin-left: 0;
    align-self: flex-end;
  }

  .error-message {
    max-width: 100%;
  }

  .detail-grid {
    grid-template-columns: 1fr;
  }

  .modal-container {
    width: 95%;
    max-height: 90vh;
  }

  .stats-summary {
    justify-content: flex-start !important;
  }
}
</style>
